import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { Button } from "../../../components/forms/Buttons/Button";
import Input from "../../../components/forms/Input/Input";
import Select from "../../../components/forms/Select/Select";

export default function AddMemberForm({
  currentMembers,
  onSubmit,
  onCancel,
  loading,
  initialData = null,
  isEditMode = false,
}) {
  const [t] = useTranslation();

  // Extract phone number without +7 prefix for editing
  const getPhoneNumberWithoutPrefix = (phoneNumber) => {
    if (!phoneNumber) return "";
    return phoneNumber.replace(/^\+7/, "");
  };

  const defaultValues = initialData
    ? {
        lastName: initialData.user?.last_name || "",
        firstName: initialData.user?.first_name || "",
        middleName: initialData.user?.middle_name || "",
        email: initialData.user?.email || "",
        memberIin: initialData.user?.username || "",
        gender: initialData.user?.gender || "",
        phone_number: getPhoneNumberWithoutPrefix(
          initialData.user?.phone_number
        ),
        // language: initialData.user?.language || "",
        grade: initialData.user?.grade || null,
      }
    : {};

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues,
  });

  useEffect(() => {
    if (isEditMode) {
      reset({
        lastName: initialData.user?.last_name || "",
        firstName: initialData.user?.first_name || "",
        middleName: initialData.user?.middle_name || "",
        email: initialData.user?.email || "",
        memberIin: initialData.user?.username || "",
        gender: initialData.user?.gender || "",
        phone_number: getPhoneNumberWithoutPrefix(
          initialData.user?.phone_number
        ),
        grade: initialData.user?.grade || null,
      });
    }
  }, [isEditMode, initialData, reset]);

  // Format phone number - only allow 10 digits (without +7)
  const formatPhoneNumber = (value) => {
    if (!value) return "";
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, "");
    // Limit to 10 digits
    return digits.slice(0, 10);
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  return (
    <div className="border-t border-gray-300 pt-6 mt-6">
      <h3 className="text-lg font-medium mb-4">
        {isEditMode ? t("team.edit_member") : t("team.add_member")}
      </h3>
      {!isEditMode && (
        <div className="mb-4 bg-primary bg-opacity-10 border border-primary/10 rounded-md p-3">
          <p className="text-sm text-primary">
            {t("team.password_will_be_sent_to_email")}
          </p>
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <Input
          label={t("signup.last_name")}
          placeholder={t("signup.enter_surname")}
          errorMsg={errors?.lastName?.message}
          inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
            errors?.lastName?.message && "ring-error ring-1"
          }`}
          className="w-full text-sm"
          type="text"
          required
          {...register("lastName", {
            required: t("signup.last_name") + " " + t("auth.email_required"),
          })}
        />

        <Input
          label={t("signup.name")}
          placeholder={t("signup.enter_name")}
          errorMsg={errors?.firstName?.message}
          inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
            errors?.firstName?.message && "ring-error ring-1"
          }`}
          className="w-full text-sm"
          type="text"
          required
          {...register("firstName", {
            required: t("signup.name") + " " + t("auth.email_required"),
          })}
        />

        <Input
          label={t("team.middle_name")}
          placeholder={t("team.enter_middle_name")}
          errorMsg={errors?.middleName?.message}
          inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
            errors?.middleName?.message && "ring-error ring-1"
          }`}
          className="w-full text-sm"
          type="text"
          {...register("middleName")}
        />

        <div>
          <Input
            label={t("student.email")}
            placeholder={t("signup.enter_email")}
            errorMsg={errors?.email?.message}
            inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
              errors?.email?.message && "ring-error ring-1"
            }`}
            className="w-full text-sm"
            type="email"
            required
            {...register("email", {
              required: t("auth.email_required"),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t("auth.invalid_email"),
              },
              validate: (value) => {
                const existingEmails = currentMembers
                  .filter((m) => !isEditMode || m.uuid !== initialData?.uuid)
                  .map((m) => m.email);
                if (existingEmails.includes(value)) {
                  return t("team.email_must_be_unique");
                }
                return true;
              },
            })}
          />
          <p className="mt-1 text-xs text-gray-500">{t("team.email_hint")}</p>
        </div>

        <Input
          label={t("auth.iin")}
          placeholder={t("auth.enter_iin")}
          errorMsg={errors?.memberIin?.message}
          inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
            errors?.memberIin?.message && "ring-error ring-1"
          }`}
          className="w-full text-sm"
          type="text"
          required
          {...register("memberIin", {
            required: t("auth.iin_required"),
            maxLength: 12,
            pattern: {
              value: /^[0-9]+$/,
              message: t("auth.invalid_iin"),
            },
          })}
        />

        <Controller
          name="gender"
          control={control}
          rules={{ required: t("signup.gender_required") }}
          render={({ field }) => {
            const genderOptions = [
              { label: t("signup.male"), value: "M" },
              { label: t("signup.female"), value: "F" },
            ];
            return (
              <Select
                label={t("signup.gender")}
                placeholder={t("signup.select_gender")}
                options={genderOptions}
                value={field.value}
                onChange={(option) => field.onChange(option?.value)}
                errorMsg={errors?.gender?.message}
                required
                className="w-full text-sm"
              />
            );
          }}
        />

        <div className="w-full text-sm">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("signup.phone_number")}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <div className="flex flex-row items-end">
            <div className="flex items-center px-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg h-10 text-gray-600">
              +7
            </div>
            <Controller
              name="phone_number"
              control={control}
              rules={{
                required: t("signup.phone_number_required"),
                validate: (value) => {
                  const digits = value?.replace(/\D/g, "") || "";
                  if (digits.length !== 10) {
                    return t("auth.invalid_phone");
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <Input
                  inputClassName={`bg-white shadow-none outline-1 outline-gray-300 border-l-0 md:rounded-r-lg md:rounded-l-none h-10 flex-1 ${
                    errors?.phone_number?.message &&
                    "ring-error ring-1 border-error"
                  }`}
                  id="phone_number"
                  name="phone_number"
                  className="flex-1"
                  placeholder="7XXXXXXXXX"
                  type="text"
                  value={field.value || ""}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    field.onChange(formatted);
                  }}
                />
              )}
            />
          </div>
          {errors?.phone_number && (
            <span className="text-xs font-semibold text-error">
              {errors.phone_number.message}
            </span>
          )}
        </div>

        {/* <Controller
          name="language"
          control={control}
          rules={{ required: t("team.language_of_study_required") }}
          render={({ field }) => {
            const languageOptions = [
              { label: "Қазақша", value: "K" },
              { label: "Русский", value: "R" },
            ];
            return (
              <Select
                label={t("signup.study_lang")}
                placeholder={t("signup.select_study_lang")}
                options={languageOptions}
                value={field.value}
                onChange={(option) => field.onChange(option?.value)}
                errorMsg={errors?.language?.message}
                required
                className="w-full text-sm"
              />
            );
          }}
        /> */}

        <Controller
          name="grade"
          control={control}
          rules={{ required: t("team.grade_required") }}
          render={({ field }) => {
            // const gradeOptions = Array.from({ length: 11 }, (_, i) => ({
            //   label: String(i + 1),
            //   value: i + 1,
            // }));
            const gradeOptions = [
              { label: t("team.grade_option", { value: 9 }), value: 9 },
              { label: t("team.grade_option", { value: 10 }), value: 10 },
              { label: t("team.grade_option", { value: 11 }), value: 11 },
              // {
              //   label: t("team.grade_option_11_5"),
              //   value: 11.5,
              // },
              { label: t("team.grade_option", { value: 12 }), value: 12 },
            ];
            return (
              <Select
                label={t("team.grade")}
                placeholder={t("team.select_grade")}
                options={gradeOptions}
                value={field.value}
                onChange={(option) => field.onChange(option?.value)}
                errorMsg={errors?.grade?.message}
                required
                className="w-full text-sm"
              />
            );
          }}
        />

        <div className="flex gap-2 mt-4">
          <Button
            text={t("buttons.cancel")}
            color="secondary"
            type="button"
            onClick={handleCancel}
          />
          <Button
            text={isEditMode ? t("buttons.save") : t("team.add_member")}
            color="primary"
            type="submit"
            loading={loading}
            disabled={loading}
          />
        </div>
      </form>
    </div>
  );
}
